import { Button, Checkbox, Form, Input, InputNumber, Radio } from 'antd'
import { FlexBox } from 'components/Box'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseFieldBlock,
  FormBaseFieldLabel,
  FormBaseSectionBlock,
} from 'components/Form/Form.styles'

interface AppropriatnessTestBuilderFormProps {
  version: number
  testType: 'appropriateness' | 'economic-profile'
  onClose: () => void
}

const AppropriatnessTestBuilderForm = ({
  onClose,
  version,
  testType,
}: AppropriatnessTestBuilderFormProps) => {
  const [form] = Form.useForm()

  const handleSaveDraft = () => {
    console.log(`Saving draft for ${testType} version ${version}`)
    const values = form.getFieldsValue()
    console.log('Form values:', values)
  }

  const handlePublish = () => {
    console.log(`Publishing ${testType} version ${version}`)
    const values = form.getFieldsValue()
    console.log('Form values:', values)
  }

  const getTestTitle = () => {
    return testType === 'economic-profile'
      ? `Economic Profile V${version}`
      : `Appropriateness Test V${version}`
  }

  const renderAppropriatenessTestFields = () => (
    <>
      <FormBaseFieldBlock>
        <FormBaseFieldLabel isRequired>Test Name</FormBaseFieldLabel>
        <Form.Item name='testName' rules={[{ required: true, message: 'Please enter test name' }]}>
          <Input placeholder='Enter test name' />
        </Form.Item>
      </FormBaseFieldBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>Question 1: What are your trading objectives?</FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='tradingObjectives'
            rules={[{ required: true, message: 'Please select your trading objectives' }]}
          >
            <Radio.Group>
              <Radio value='capital-preservation'>Capital Preservation</Radio>
              <Radio value='income-generation'>Income Generation</Radio>
              <Radio value='capital-growth'>Capital Growth</Radio>
              <Radio value='speculation'>Speculation</Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='tradingObjectivesScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>Question 2: What is your risk tolerance level?</FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='riskTolerance'
            rules={[{ required: true, message: 'Please select your risk tolerance' }]}
          >
            <Radio.Group>
              <Radio value='conservative'>Conservative - I prefer low risk investments</Radio>
              <Radio value='moderate'>Moderate - I can accept some risk for better returns</Radio>
              <Radio value='aggressive'>
                Aggressive - I am comfortable with high risk investments
              </Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='riskToleranceScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>
          Question 3: How much trading experience do you have?
        </FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='tradingExperience'
            rules={[{ required: true, message: 'Please select your trading experience' }]}
          >
            <Radio.Group>
              <Radio value='none'>No experience - I am new to trading</Radio>
              <Radio value='limited'>Limited experience - Less than 1 year</Radio>
              <Radio value='moderate'>Moderate experience - 1-3 years</Radio>
              <Radio value='extensive'>Extensive experience - More than 3 years</Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='tradingExperienceScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseFieldBlock>
        <FormBaseFieldLabel isRequired>Total Passing Score (%)</FormBaseFieldLabel>
        <Form.Item
          name='passingScore'
          rules={[{ required: true, message: 'Please enter total passing score' }]}
        >
          <InputNumber min={0} max={300} placeholder='Enter total passing score (max 300)' />
        </Form.Item>
      </FormBaseFieldBlock>

      <FormBaseFieldBlock>
        <FormBaseFieldLabel>Is Active</FormBaseFieldLabel>
        <Form.Item name='isActive' valuePropName='checked'>
          <Checkbox>Make this test active</Checkbox>
        </Form.Item>
      </FormBaseFieldBlock>
    </>
  )

  const renderEconomicProfileFields = () => (
    <>
      <FormBaseFieldBlock>
        <FormBaseFieldLabel isRequired>Profile Name</FormBaseFieldLabel>
        <Form.Item
          name='profileName'
          rules={[{ required: true, message: 'Please enter profile name' }]}
        >
          <Input placeholder='Enter economic profile name' />
        </Form.Item>
      </FormBaseFieldBlock>

      <FormBaseFieldBlock>
        <FormBaseFieldLabel>Profile Description</FormBaseFieldLabel>
        <Form.Item
          name='profileDescription'
          rules={[{ required: true, message: 'Please enter profile description' }]}
        >
          <Input.TextArea rows={4} placeholder='Enter profile description' />
        </Form.Item>
      </FormBaseFieldBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>Question 1: What are your investment objectives?</FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='investmentObjectives'
            rules={[{ required: true, message: 'Please select your investment objectives' }]}
          >
            <Radio.Group>
              <Radio value='capital-preservation'>Capital Preservation</Radio>
              <Radio value='income-generation'>Income Generation</Radio>
              <Radio value='capital-growth'>Capital Growth</Radio>
              <Radio value='speculation'>Speculation</Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='investmentObjectivesScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>Question 2: What is your annual income range?</FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='annualIncome'
            rules={[{ required: true, message: 'Please select your annual income range' }]}
          >
            <Radio.Group>
              <Radio value='0-25000'>$0 - $25,000</Radio>
              <Radio value='25000-50000'>$25,000 - $50,000</Radio>
              <Radio value='50000-100000'>$50,000 - $100,000</Radio>
              <Radio value='100000+'>$100,000+</Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='annualIncomeScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseSectionBlock>
        <FormBaseFieldLabel>Question 3: What is your net worth range?</FormBaseFieldLabel>
        <FormBaseFieldBlock>
          <Form.Item
            name='netWorth'
            rules={[{ required: true, message: 'Please select your net worth range' }]}
          >
            <Radio.Group>
              <Radio value='0-50000'>$0 - $50,000</Radio>
              <Radio value='50000-250000'>$50,000 - $250,000</Radio>
              <Radio value='250000-1000000'>$250,000 - $1,000,000</Radio>
              <Radio value='1000000+'>$1,000,000+</Radio>
            </Radio.Group>
          </Form.Item>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseFieldLabel isRequired>Score for this question</FormBaseFieldLabel>
          <Form.Item
            name='netWorthScore'
            rules={[{ required: true, message: 'Please enter score' }]}
          >
            <InputNumber min={0} max={100} placeholder='Enter score (0-100)' />
          </Form.Item>
        </FormBaseFieldBlock>
      </FormBaseSectionBlock>

      <FormBaseFieldBlock>
        <FormBaseFieldLabel>Is Active</FormBaseFieldLabel>
        <Form.Item name='isActive' valuePropName='checked'>
          <Checkbox>Make this profile active</Checkbox>
        </Form.Item>
      </FormBaseFieldBlock>
    </>
  )

  return (
    <>
      <FlexBox justifyContent='space-between' alignItems='center' marginBottom={20}>
        <h2>{getTestTitle()}</h2>
        <FormBaseButtonContainer>
          <Button type='primary' onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSaveDraft}>Save draft</Button>
          <Button onClick={handlePublish}>Publish</Button>
        </FormBaseButtonContainer>
      </FlexBox>

      <FormBaseContainer>
        <Form
          form={form}
          layout='vertical'
          style={{ maxWidth: 800 }}
          initialValues={{
            isActive: false,
          }}
        >
          {testType === 'economic-profile'
            ? renderEconomicProfileFields()
            : renderAppropriatenessTestFields()}
        </Form>
      </FormBaseContainer>
    </>
  )
}

export default AppropriatnessTestBuilderForm
